import React, { useMemo, useState } from 'react';

function TableWrapper({ title, children, right }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        </div>
        {right}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export default function CajaTables({ detalle = [], agrupado = [], formatMoney, formatDateTime, toNumber }) {
  const [activeTab, setActiveTab] = useState('detalle');
  const [filterText] = useState('');

  const filteredDetalle = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return detalle;

    return detalle.filter((row) => {
      const cliente = String(row?.cliente_nombre || '').toLowerCase();
      const dni = String(row?.cliente_dni || '').toLowerCase();
      const cuota = String(row?.numero_cuota ?? '').toLowerCase();
      const operacion = String(row?.numero_operacion ?? '').toLowerCase();
      const prestamo = String(row?.idPrestamo ?? '').toLowerCase();
      return (
        cliente.includes(query) ||
        dni.includes(query) ||
        cuota.includes(query) ||
        operacion.includes(query) ||
        prestamo.includes(query)
      );
    });
  }, [detalle, filterText]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('detalle')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'detalle'
                ? 'bg-red-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Detalle
          </button>
          <button
            onClick={() => setActiveTab('operacion')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'operacion'
                ? 'bg-red-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Por operacion
          </button>
        </div>

      </div>

      <div className="p-5">
        {activeTab === 'operacion' ? (
          <TableWrapper title="Agrupado por N. de operacion">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-700">
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">N. operacion</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">Total</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]"># pagos</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">Clientes</th>
                </tr>
              </thead>
              <tbody>
                {agrupado.length === 0 ? (
                  <tr>
                    <td className="py-8 px-4 text-center text-slate-500" colSpan={4}>
                      Sin datos
                    </td>
                  </tr>
                ) : (
                  agrupado.map((row) => (
                    <tr
                      key={row.numero_operacion || 'sin-op'}
                      className="border-b border-slate-100 transition last:border-b-0 hover:bg-slate-50"
                    >
                      <td className="py-3 px-3 text-center font-medium text-slate-900">{row.numero_operacion || 'N/A'}</td>
                      <td className="py-3 px-3 text-center font-semibold text-slate-900">{formatMoney(row.total_monto_pagado)}</td>
                      <td className="py-3 px-3 text-center">{row.cantidad_pagos}</td>
                      <td className="py-3 px-3 text-center text-xs text-slate-700">{row.clientes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrapper>
        ) : (
          <TableWrapper
            title="Detalle de pagos"
          >
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-700">
                  <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-[0.1em]">Cliente</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">Prestamo</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">N. cuota</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">N. operacion</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">Valor cuota</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">Monto abonado</th>
                  <th className="py-3 px-3 text-center text-xs font-semibold uppercase tracking-[0.1em]">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredDetalle.length === 0 ? (
                  <tr>
                    <td className="py-8 px-4 text-center text-slate-500" colSpan={7}>
                      {detalle.length === 0 ? 'Sin pagos registrados' : 'Sin resultados para el filtro'}
                    </td>
                  </tr>
                ) : (
                  filteredDetalle.map((row) => {
                    const montoCuota = toNumber(row.monto_cuota) + toNumber(row.mora_cuota);
                    const fecha = row.created_at || row.fecha_pago;

                    return (
                      <tr key={row.idPago} className="border-b border-slate-100 transition last:border-b-0 hover:bg-slate-50">
                        <td className="py-3 px-3">
                          <div className="flex flex-col">
                            <span className="text-xs font-medium text-slate-900">{row.cliente_nombre}</span>
                            <span className="text-xs text-slate-500">DNI: {row.cliente_dni}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center font-medium text-slate-900">#{row.idPrestamo}</td>
                        <td className="py-3 px-3 text-center">{row.numero_cuota}</td>
                        <td className="py-3 px-3 text-center">{row.numero_operacion || 'N/A'}</td>
                        <td className="py-3 px-3 text-center font-semibold text-slate-900">{formatMoney(montoCuota)}</td>
                        <td className="py-3 px-3 text-center font-semibold text-slate-900">{formatMoney(row.monto_pagado)}</td>
                        <td className="py-3 px-3 text-center text-xs text-slate-700">{formatDateTime(fecha)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </TableWrapper>
        )}
      </div>
    </div>
  );
}
